import { isBefore, startOfToday } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';

import {
  getFilteredItems,
  getItemsByDay,
} from '../components/scheduleTabs/helpers/getFilteredItems';
import {
  getDateTimeBoundaries,
  getLocalisedDate,
  ItemWithTimeStatus,
  TimeStatus,
} from '../helpers';
import { useShowSeconds } from '../hooks/useShowSeconds';
import { PartSchedule } from '../types';

const DEBOUNCE_TIME = 125;

export interface ScheduleTabsData {
  itemsByDay: [string, ItemWithTimeStatus[]][];
  scheduleStart: Date;
  scheduleEnd: Date;
  setSearchValue: (val: string) => void;
  setSelectedTags: (tags: string[]) => void;
  setSelectedParts: (parts: string[]) => void;
  setSelectedEventTypes: (types: string[]) => void;
  setSelectedSpeakers: (types: string[]) => void;
  setSelectedTimeStatuses: (timeStatuses: TimeStatus[]) => void;
  setSelectedEditingStatuses: (editingStatuses: string[]) => void;
  searchValue: string;
  selectedTags: string[];
  selectedParts: string[];
  selectedEventTypes: string[];
  selectedSpeakers: string[];
  selectedTimeStatuses: TimeStatus[];
  selectedEditingStatuses: string[];
  timezone: string;
  activeIndex: number;
  setTabIndex: (index: number) => void;
  scheduleContext: 'organiser' | 'registration' | 'reception' | 'in-event';
  showSeconds: boolean;
  toggleShowSeconds: () => void;
}

export const ScheduleTabsContext = createContext<ScheduleTabsData>(
  {} as ScheduleTabsData,
);

export const ScheduleTabsProvider: React.FC<{
  schedule?: PartSchedule;
  scheduleContext: ScheduleTabsData['scheduleContext'];
}> = ({ schedule, scheduleContext, children }) => {
  const { time_start, time_end, timezone = 'Etc/UTC' } = schedule?.time || {};
  const { scheduleStart, scheduleEnd } = getDateTimeBoundaries({
    min:
      time_start && timezone ? utcToZonedTime(time_start, timezone) : undefined,
    max: time_end && timezone ? utcToZonedTime(time_end, timezone) : undefined,
  });

  const [searchValue, setSearchValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
  const [selectedParts, setSelectedParts] = useState<string[]>([]);
  const [selectedTimeStatuses, setSelectedTimeStatuses] = useState<
    TimeStatus[]
  >(['live', 'future']);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedEditingStatuses, setSelectedEditingStatuses] = useState<
    string[]
  >([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [debouncedSearchValue] = useDebounce(searchValue, DEBOUNCE_TIME);

  const items = useMemo(
    () =>
      (scheduleContext === 'organiser'
        ? schedule?.items
        : schedule?.items.filter(
            item =>
              item.event_part_type !== 'custom' ||
              (schedule.organiser?.custom_event_part_title &&
                schedule.organiser?.custom_event_part_url),
          )) || [],
    [
      scheduleContext,
      schedule?.items,
      schedule?.organiser?.custom_event_part_title,
      schedule?.organiser?.custom_event_part_url,
    ],
  );

  const filteredItems = useMemo(
    () =>
      getFilteredItems({
        items,
        search: searchValue,
        selectedTags,
        selectedParts,
        selectedEventTypes,
        selectedSpeakers,
        selectedTimeStatuses,
        selectedEditingStatuses,
      }),
    [
      items,
      searchValue,
      selectedEventTypes,
      selectedParts,
      selectedTags,
      selectedSpeakers,
      selectedTimeStatuses,
      selectedEditingStatuses,
    ],
  );

  const [itemsByDay, setItemsByDay] = useState<
    [string, ItemWithTimeStatus[]][]
  >(
    getItemsByDay({
      start: scheduleStart,
      end: scheduleEnd,
      items,
      filteredItems,
      timezone: schedule?.time?.timezone,
    }),
  );

  const buildItemsByDay = () => {
    setItemsByDay(
      getItemsByDay({
        start: scheduleStart,
        end: scheduleEnd,
        items,
        filteredItems,
        timezone: schedule?.time?.timezone,
      }),
    );
  };

  useEffect(
    buildItemsByDay,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      items,
      debouncedSearchValue,
      selectedTags,
      selectedParts,
      selectedEventTypes,
      selectedSpeakers,
      selectedTimeStatuses,
      selectedEditingStatuses,
    ],
  );

  const setTabIndex = useCallback(
    (index: number) => {
      const firstItem = itemsByDay[index]?.[0];
      if (firstItem && isBefore(getLocalisedDate(firstItem), startOfToday())) {
        setSelectedTimeStatuses(
          Array.from(new Set([...selectedTimeStatuses, 'past' as TimeStatus])),
        );
      }
      setActiveIndex(index);
    },
    [itemsByDay, selectedTimeStatuses, setSelectedTimeStatuses],
  );

  const { showSeconds, toggleShowSeconds } = useShowSeconds();

  return (
    <ScheduleTabsContext.Provider
      value={{
        itemsByDay,
        scheduleStart,
        scheduleEnd,
        searchValue,
        setSearchValue,
        selectedEventTypes,
        setSelectedEventTypes,
        selectedParts,
        setSelectedParts,
        selectedTags,
        setSelectedTags,
        selectedSpeakers,
        setSelectedSpeakers,
        selectedTimeStatuses,
        setSelectedTimeStatuses,
        selectedEditingStatuses,
        setSelectedEditingStatuses,
        timezone,
        activeIndex,
        setTabIndex,
        scheduleContext,
        showSeconds,
        toggleShowSeconds,
      }}
    >
      {children}
    </ScheduleTabsContext.Provider>
  );
};
